import { ReactNode, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import inflection from "inflection";

import { Stack, Shelf, Grid, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Body, Heading, Semibold, Subtitle } from "capitalrx-components-2.0/lib/typography";
import { formatDollars, formatPhoneNumber } from "capitalrx-utilities";
import { Color, Tooltip, useAlert } from "capitalrx-components-2.0/lib/general";

import { PageLayout, Breadcrumbs, NeedHelp, Card } from "@/premium/themedComponents";
import { ThemedH2 } from "@/common/components/themed/typography";
import { AppleMap } from "@/basic/components";
import { getPharmacy } from "@/api/axios/pharmacies";
import { PharmacyInfo } from "@/api/types";

import { DrugDetailsSection } from "./DrugDetailsSection";
import "./PaymentDetails.scss";

interface PaymentBarEntry {
  quantity: number;
  color: string;
  tooltip: ReactNode;
}

interface PaymentBarsProps {
  entries: PaymentBarEntry[]
}

const PaymentBars = ({ entries }: PaymentBarsProps) => {
  const total = entries.reduce((total, entry) => total + entry.quantity, 0);

  if (Number.isFinite(total) && Math.abs(total) < 0.005) {
    return <></>;
  }

  return <Shelf spacing="mini">
    {entries.map((entry, idx) => (
      <Tooltip
        alignment="top"
        content={entry.tooltip}
        key={"PaymentBar" + idx}
      >
        {(ref) => (
          <div
            ref={ref as React.Ref<HTMLDivElement>}
            style={{
              backgroundColor: entry.color,
              width: `calc(${entry.quantity / total * 100}%)`,
              maxWidth: "100%",
              height: "35px",
            }}
          />
        )}
      </Tooltip>
    ))}
  </Shelf>;
};
export const PharmacyDetailsPage = () => {
  const showAlert = useAlert();
  const [pharmacyData, setPharmacyData] = useState<PharmacyInfo>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t: translate } = useTranslation();
  const { columns, isMobile } = useResponsive({
    columns: {
      desktop: 6,
      tablet: 6,
      mobile: 12,
    },
    isMobile: {
      desktop: false,
      tablet: false,
      mobile: true,
    },
  });

  const days_supply = +searchParams.get("daysSupply");
  const dosage = searchParams.get("dosage");
  const quantity = +searchParams.get("quantity");
  const dosage_form = searchParams.get("dosageForm");
  const drug_name = searchParams.get("drugName");
  const drug_type = searchParams.get("drugType");
  const providerId = searchParams.get("providerId");

  const patientPayAmount = decodeURIComponent(searchParams.get("patientPay") || "");
  const planPayAmount = decodeURIComponent(searchParams.get("planPay") || "");
  const formattedPatientPayAmount = formatDollars(patientPayAmount || "0");

  const formattedPlanPayAmount = formatDollars(planPayAmount || "0");

  useEffect(() => {
    async function fetchData() {
      try {
        const pharmacy = await getPharmacy({ provider_id: providerId });
        setPharmacyData(pharmacy);
      } catch (e) {
        showAlert({
          message: translate("FIND_PHARMACIES.findPharmacies.mapsLoadingError"),
          type: "error",
          style: "banner",
        });
      }
    }
    fetchData();
  }, [providerId]);

  const handleBackButtonClicked = () => {
    navigate(-1);
  };

  const entries = useMemo(() => {
    const entries = [];

    if (+patientPayAmount >= 0.005) {
      entries.push({
        color: Color.toString(Color.Turqoise["600"]),
        quantity: +patientPayAmount,
        tooltip: (<span>
          {translate("FIND_PHARMACIES.paymentDetails.youWouldPay")}: <Semibold>{formattedPatientPayAmount}</Semibold>
        </span>),
      });
    }

    if (+planPayAmount >= 0.005) {
      entries.push({
        color: Color.toString(Color.Green["500"]),
        quantity: +planPayAmount,
        tooltip: (<span>
          {translate("FIND_PHARMACIES.paymentDetails.planWouldPay")}: <Semibold>{formattedPlanPayAmount}</Semibold>
        </span>),
      });
    }

    return entries;
  }, [patientPayAmount, planPayAmount]);

  const renderAddress = () => {
    const hasAddressLine2 = !!pharmacyData?.address_line_2;
    const hasCityStateZip = pharmacyData?.city && pharmacyData?.state && pharmacyData?.zip;

    return (
      <Body size="medium">
        {pharmacyData?.address_line_1}
        {hasAddressLine2 && <span><br />{pharmacyData?.address_line_2}</span>}
        {hasCityStateZip && <span><br />{pharmacyData?.city}, {pharmacyData?.state}, {pharmacyData?.zip}</span>}
      </Body>
    );
  };

  return (
    <PageLayout>
      <Stack spacing={"medium-large"}>
        <Breadcrumbs />
        <Shelf>
          <Stack spacing="none">
            <ThemedH2>{translate("FIND_PHARMACIES.findPharmacies.pharmacy")} {translate("FIND_PHARMACIES.findPharmacies.andDrugPricing")}</ThemedH2>
          </Stack>
          <Shelf.Spacer />
        </Shelf>
        <Card noPadding>
          <DrugDetailsSection
            drugDetails={{
              dosage,
              dosage_form,
              days_supply,
              quantity,
              drug_name,
              drug_type,
            }}
            onBackButtonClicked={handleBackButtonClicked}
          />
          <Stack spacing="large" style={{ padding: "1.5rem" }}>
            <Grid spacing="none">
              <Grid.Span columns={12}>
                <Shelf spacing="small">
                  <div className="leftLine">
                    <div />
                  </div>
                  <Stack spacing="none" className="leftStack">
                    <Semibold>{translate("FIND_PHARMACIES.paymentDetails.youWouldPay")}</Semibold>
                    <Subtitle testId="patient-pay-amount" textAlign="right">
                      {formattedPatientPayAmount}
                    </Subtitle>
                  </Stack>
                  <div className="midContainer">
                    <div />
                  </div>
                  <Stack spacing="none" className="rightStack">
                    <Semibold>{translate("FIND_PHARMACIES.paymentDetails.planWouldPay")}</Semibold>
                    <Subtitle testId="plan-pay-amount" className="plan-pay">
                      {formattedPlanPayAmount}
                    </Subtitle>
                  </Stack>
                  <div className="rightLine">
                    <div />
                  </div>
                </Shelf>
              </Grid.Span>

              <Grid.Span columns={12}>
                <PaymentBars entries={entries} />
              </Grid.Span>
            </Grid>
            <Grid spacing="large">
              {/* Provider Summary */}
              <Grid.Span columns={columns}>
                <Stack spacing={isMobile ? "large" : "medium"}>
                  <Heading>{translate("PHARMACY_DETAILS_CONTAINER.pharmacyDetails")}</Heading>
                  <Grid spacing="small">
                    <Grid.Span columns={4}>
                      <Body size="medium"><Semibold>{translate("PHARMACY_DETAILS_CONTAINER.name")}:</Semibold></Body>
                    </Grid.Span>
                    <Grid.Span columns={8}>
                      <Body size="medium">{pharmacyData?.name}</Body>
                    </Grid.Span>
                    <Grid.Span columns={4}>
                      <Body size="medium"><Semibold>{translate("PHARMACY_DETAILS_CONTAINER.address")}:</Semibold></Body>
                    </Grid.Span>
                    <Grid.Span columns={8}>
                      <Body size="medium">{renderAddress()}</Body>
                    </Grid.Span>
                    <Grid.Span columns={4}>
                      <Body size="medium"><Semibold>{translate("PHARMACY_DETAILS_CONTAINER.phone")}:</Semibold></Body>
                    </Grid.Span>
                    <Grid.Span columns={8}>
                      <Body size="medium">{formatPhoneNumber(pharmacyData?.phone)}</Body>
                    </Grid.Span>
                    <Grid.Span columns={4}>
                      <Body size="medium"><Semibold>{translate("PHARMACY_DETAILS_CONTAINER.hours")}:</Semibold></Body>
                    </Grid.Span>
                    <Grid.Span columns={8}>
                      {(pharmacyData?.hours || []).map((hour) => {
                        return <Body size="medium" key={hour}>{hour}</Body>;
                      })}
                    </Grid.Span>
                    <Grid.Span columns={4}>
                      <Body size="medium"><Semibold>{translate("PHARMACY_DETAILS_CONTAINER.fluShots")}:</Semibold></Body>
                    </Grid.Span>
                    <Grid.Span columns={8}>
                      <Body size="medium">{pharmacyData?.services.immunizations_provided_indicator || "No"}</Body>
                    </Grid.Span>
                    <Grid.Span columns={4}>
                      <Body size="medium"><Semibold>{translate("PHARMACY_DETAILS_CONTAINER.driveThru")}:</Semibold></Body>
                    </Grid.Span>
                    <Grid.Span columns={8}>
                      <Body size="medium">{pharmacyData?.services.drive_up_window_indicator || "No"} </Body>
                    </Grid.Span>
                    <Grid.Span columns={4}>
                      <Body size="medium"><Semibold>{translate("PHARMACY_DETAILS_CONTAINER.languages")}:</Semibold></Body>
                    </Grid.Span>
                    <Grid.Span columns={8}>
                      {pharmacyData?.languages.length > 0 && <Body size="medium">{pharmacyData?.languages.join(", ")}</Body>}
                      {pharmacyData?.languages.length === 0 && <Body size="medium">N/A</Body>}
                    </Grid.Span>
                  </Grid>
                </Stack>
              </Grid.Span>
              {/* Drug Details */}
              <Grid.Span columns={columns}>
                <Stack spacing={isMobile ? "large" : "medium"}>
                  <Heading>{translate("FIND_PHARMACIES.drugDetailsForm.prescriptionDetails")}</Heading>
                  <Grid spacing="small">
                    <Grid.Span columns={4}>
                      <Body size="medium"><Semibold>{translate("FIND_PHARMACIES.drugDetailsForm.name")}:</Semibold></Body>
                    </Grid.Span>
                    <Grid.Span columns={8}><Body size="medium">{drug_name}</Body></Grid.Span>
                    <Grid.Span columns={4}>
                      <Body size="medium"><Semibold>{translate("FIND_PHARMACIES.drugDetailsForm.type")}:</Semibold></Body>
                    </Grid.Span>
                    <Grid.Span columns={8}><Body size="medium">{drug_type}</Body></Grid.Span>
                    <Grid.Span columns={4}>
                      <Body size="medium"><Semibold>{translate("FIND_PHARMACIES.drugDetailsForm.quantity")}:</Semibold></Body>
                    </Grid.Span>
                    <Grid.Span columns={8}><Body size="medium">{inflection.inflect(`${Number(Number(quantity.toFixed(3)))} ${dosage_form}`, quantity)}</Body></Grid.Span>
                    <Grid.Span columns={4}>
                      <Body size="medium"><Semibold>{translate("FIND_PHARMACIES.drugDetailsForm.daysSupply")}:</Semibold></Body>
                    </Grid.Span>
                    <Grid.Span columns={8}><Body size="medium">{days_supply} {translate("FIND_PHARMACIES.drugDetailsForm.days")}</Body></Grid.Span>
                    <Grid.Span columns={4}>
                      <Body size="medium"><Semibold>{translate("FIND_PHARMACIES.drugDetailsForm.dosage")}:</Semibold></Body>
                    </Grid.Span>
                    <Grid.Span columns={8}><Body size="medium">{dosage}</Body></Grid.Span>
                    <Grid.Span columns={4}>
                      <Body size="medium"><Semibold>{translate("FIND_PHARMACIES.drugDetailsForm.form")}:</Semibold></Body>
                    </Grid.Span>
                    <Grid.Span columns={8}><Body size="medium">{dosage_form}</Body></Grid.Span>
                  </Grid>
                </Stack>
              </Grid.Span>

            </Grid>
            {pharmacyData && <AppleMap
              style={{
                height: "400px",
              }}
              locations={[{
                latitude: parseFloat(pharmacyData.latitude),
                longitude: parseFloat(pharmacyData.longitude),
                title: pharmacyData.name,
              }]}
            />}
          </Stack>
        </Card>
        <NeedHelp />
      </Stack>
    </PageLayout>
  );
};
